import { Graph, Txn } from "./graph";
import { nearestEdge, nearestNode, segmentIntersections } from "./snap";

/**
 * Resolve a pointer position to a node id, creating geometry as needed:
 * snaps to an existing node, else splits a nearby edge, else adds a free node.
 * snapDist is in image pixels; 0 disables snapping.
 */
export function resolveAnchor(g: Graph, txn: Txn, x: number, y: number, snapDist: number): number {
  if (snapDist > 0) {
    const n = nearestNode(g, x, y, snapDist);
    if (n !== null) return n;
    const hit = nearestEdge(g, x, y, snapDist);
    if (hit) return txn.splitEdge(hit.edgeId, hit.x, hit.y);
  }
  return txn.addNode(x, y);
}

/**
 * Connect two existing nodes with an edge of the given class. When
 * snapIntersections is on, every proper crossing with an existing edge becomes
 * a shared node splitting both lines.
 */
export function insertEdge(
  g: Graph,
  txn: Txn,
  aId: number,
  bId: number,
  classId: number,
  snapIntersections: boolean,
): void {
  const a = g.nodes.get(aId);
  const b = g.nodes.get(bId);
  if (!a || !b || aId === bId) return;
  if (!snapIntersections) {
    txn.addEdge(aId, bId, classId);
    return;
  }
  const crossings = segmentIntersections(g, a.x, a.y, b.x, b.y, new Set([aId, bId]), new Set());
  let prev = aId;
  for (const c of crossings) {
    const mid = txn.splitEdge(c.edgeId, c.x, c.y);
    txn.addEdge(prev, mid, classId);
    prev = mid;
  }
  txn.addEdge(prev, bId, classId);
}

/** Delete whatever is near (x, y): a node (with its edges) or an edge. Returns true if something was deleted. */
export function deleteAt(g: Graph, txn: Txn, x: number, y: number, r: number): boolean {
  const n = nearestNode(g, x, y, r);
  if (n !== null) {
    const neighbors: number[] = [];
    for (const e of g.edgesOf(n)) neighbors.push(e.a === n ? e.b : e.a);
    txn.delNodeWithEdges(n);
    txn.pruneIsolated(neighbors);
    return true;
  }
  const hit = nearestEdge(g, x, y, r);
  if (hit) {
    const e = g.edges.get(hit.edgeId)!;
    txn.delEdge(hit.edgeId);
    txn.pruneIsolated([e.a, e.b]);
    return true;
  }
  return false;
}

/**
 * Connect a dangling line end (degree-1 node) to nearby geometry:
 * 1. extend the line along its own direction; the first edge the extension
 *    crosses within maxDist gets split there and connected, else
 * 2. fall back to the closest point on any edge within maxDist of the endpoint
 *    (snap-merge if practically touching, short connector otherwise).
 * Returns true if a connection was made.
 */
export function extendDangling(g: Graph, txn: Txn, nodeId: number, maxDist: number): boolean {
  const n = g.nodes.get(nodeId);
  if (!n || g.degree(nodeId) !== 1) return false;
  const e = g.edgesOf(nodeId)[0];
  const p = g.nodes.get(e.a === nodeId ? e.b : e.a)!;
  const dx = n.x - p.x;
  const dy = n.y - p.y;
  const len = Math.hypot(dx, dy);
  const skipEdges = new Set([e.i]);
  if (len > 1e-9) {
    const ux = dx / len;
    const uy = dy / len;
    const hits = segmentIntersections(
      g, n.x, n.y, n.x + ux * maxDist, n.y + uy * maxDist, new Set([nodeId]), skipEdges,
    );
    if (hits.length) {
      const first = hits[0];
      const mid = txn.splitEdge(first.edgeId, first.x, first.y);
      if (Math.hypot(first.x - n.x, first.y - n.y) < 1.5) {
        mergeNodes(g, txn, nodeId, mid); // practically touching: no micro-edge
      } else {
        txn.addEdge(nodeId, mid, e.c);
      }
      return true;
    }
  }
  const hit = nearestEdge(g, n.x, n.y, maxDist, skipEdges);
  if (hit) {
    const mid = txn.splitEdge(hit.edgeId, hit.x, hit.y);
    if (hit.dist < 1.5) {
      mergeNodes(g, txn, nodeId, mid); // touching already: fuse instead of a micro-edge
    } else {
      txn.addEdge(nodeId, mid, e.c);
    }
    return true;
  }
  return false;
}

/**
 * After nodes were moved: wherever their incident edges now properly cross
 * another edge, split both lines at the crossing into a shared node — the
 * same connectivity guarantee drawing gives via insertEdge. Returns the
 * number of intersections created.
 */
export function resolveCrossings(g: Graph, txn: Txn, movedNodes: Iterable<number>): number {
  const edgeIds = new Set<number>();
  for (const nid of movedNodes) {
    for (const e of g.edgesOf(nid)) edgeIds.add(e.i);
  }
  let count = 0;
  for (const ei of edgeIds) {
    const e = g.edges.get(ei);
    if (!e) continue; // already split while resolving an earlier edge
    const a = g.nodes.get(e.a);
    const b = g.nodes.get(e.b);
    if (!a || !b) continue;
    const crossings = segmentIntersections(g, a.x, a.y, b.x, b.y, new Set([e.a, e.b]), new Set([e.i]));
    if (!crossings.length) continue;
    txn.delEdge(e.i);
    let prev = e.a;
    for (const c of crossings) {
      const mid = txn.splitEdge(c.edgeId, c.x, c.y);
      txn.addEdge(prev, mid, e.c);
      prev = mid;
      count++;
    }
    txn.addEdge(prev, e.b, e.c);
  }
  return count;
}

/** Merge node `src` into node `dst`: reconnect all edges, drop duplicates/self-loops. */
export function mergeNodes(g: Graph, txn: Txn, src: number, dst: number): void {
  for (const e of g.edgesOf(src)) {
    const other = e.a === src ? e.b : e.a;
    txn.delEdge(e.i);
    if (other !== dst) txn.addEdge(other, dst, e.c);
  }
  txn.delNodeWithEdges(src);
}
